<script>
  import { onMount } from 'svelte';
  import Swiper from 'swiper';
  import 'swiper/swiper-bundle.css';
  import { navigate } from "svelte-routing";

  export let title;
  export let contentItems = [];
  export let subtitle = "";

  let swiperContainer;
  let paginationEl;

  onMount(() => {
    const swiper = new Swiper(swiperContainer, {
      loop: true,
      slidesPerView: 3,
      spaceBetween: 30,
      autoHeight: false,
      breakpoints: {
        0: {
          slidesPerView: 1.08,
          spaceBetween: 16
        },
        700: {
          slidesPerView: 2.1,
          spaceBetween: 20
        },
        1100: {
          slidesPerView: 3,
          spaceBetween: 30
        }
      },
      pagination: {
        el: paginationEl,
        clickable: true,
      },
    });

    return () => swiper.destroy(true, true);
  });

  function goToContentDetail(id) {
    navigate(`/content/${id}`);
  }

  function handleKeydown(event, id) {
    if (event.key === 'Enter' || event.key === ' ') {
      goToContentDetail(id);
    }
  }
</script>

<div class="carousel-section">
  <div class="section-heading mb-lg">
    <div>
      <span class="font-label-md text-title uppercase tracking-wider mb-2 block">Collection</span>
      <h2 class="font-headline-lg text-title">{title}</h2>
    </div>
    {#if subtitle}
      <p class="text-muted font-body-md mt-2">{subtitle}</p>
    {/if}
  </div>
  <div class="swiper-container" bind:this={swiperContainer}>
    <div class="swiper-wrapper">
      {#each contentItems as item (item.id)}
        <div
          class="swiper-slide slide-panel"
          on:click={() => goToContentDetail(item.id)}
          on:keydown={(e) => handleKeydown(e, item.id)}
          style="cursor: pointer;"
          tabindex="0"
          role="button"
        >
          <div class="slide-top">
            <span class="slide-date">{item.date}</span>
            <span class="slide-arrow">↗</span>
          </div>
          <h3>{item.title}</h3>
          <p class="description">{item.description}</p>
          <p class="tags">#{item.tags.length}</p>
        </div>
      {/each}
      {#if contentItems.length === 0}
        <div class="swiper-slide">콘텐츠 없음</div>
      {/if}
    </div>
    <div class="swiper-pagination" bind:this={paginationEl}></div>
  </div>
</div>

<style>
  .carousel-section {
    display: grid;
    gap: var(--spacing-lg);
  }

  .section-heading h2 {
    font-size: 28px;
    font-weight: 600;
    line-height: 36px;
    letter-spacing: -0.02em;
    color: var(--text);
  }

  .section-heading p {
    color: var(--muted);
    font-size: 16px;
  }

  .swiper-container {
    width: 100%;
    overflow: hidden;
    padding: var(--spacing-xs) 4px var(--spacing-xl);
    position: relative;
  }

  .swiper-container :global(.swiper-wrapper) {
    align-items: stretch;
  }

  .swiper-container :global(.swiper-slide) {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: var(--spacing-md);
    height: 340px !important;
    padding: var(--spacing-lg);
    border-radius: var(--radius-md);
    text-align: left;
    background: var(--surface);
    border: 1px solid var(--line);
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
    position: relative;
    overflow: hidden;
  }

  .swiper-container :global(.swiper-slide:hover) {
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
    border-color: var(--outline);
  }

  .slide-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: var(--muted);
    font-family: var(--font-mono);
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.05em;
  }

  .slide-arrow {
    width: 36px;
    height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--radius-sm);
    background: var(--surface-container);
    color: var(--text);
    font-size: 1rem;
    border: 1px solid var(--line);
    transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
  }

  :global(.swiper-container .swiper-slide:hover .slide-arrow) {
      background: var(--primary);
      color: var(--on-primary);
      border-color: var(--primary);
  }

  .swiper-container :global(.swiper-slide h3) {
    font-size: 20px;
    font-weight: 600;
    line-height: 28px;
    color: var(--text);
    letter-spacing: -0.01em;
    max-width: 14ch;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .description {
    font-size: 14px;
    color: var(--muted);
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: 22px;
  }

  .tags {
    font-family: var(--font-mono);
    font-size: 12px;
    font-weight: 600;
    color: var(--badge-text);
    background: var(--badge-bg);
    padding: 4px 10px;
    border-radius: var(--radius-sm);
    align-self: flex-start;
    margin-top: auto;
    letter-spacing: 0.02em;
  }

  :global(.swiper-pagination-bullet) {
    width: 24px;
    height: 4px;
    background: var(--line);
    opacity: 1;
    border-radius: 2px;
    transition: background-color 0.2s ease;
  }

  :global(.swiper-pagination-bullet-active) {
    background: var(--primary);
  }

  @media (max-width: 900px) {
    .swiper-container :global(.swiper-slide) {
      height: 300px;
      padding: var(--spacing-md);
    }

    .swiper-container :global(.swiper-slide h3) {
      font-size: 18px;
      line-height: 26px;
    }
  }
</style>
